import type {
    OrderState,
    OrderCompany,
    OrderCollection,
    OrderComposition,
    OrderIssuer,
    OrderPrice,
    OrderStyle,
    OrderLabel,
    OrderKeyOrder,
    OrderUser,
    OrderStandardText,
    OrderAssortment,
    OrderCustomerGroup,
    OrderDeliveryPeriod,
    OrderHeaderStandardText,
} from '../../gql';

import {
    randCompanyName,
    randNumber,
    randWord,
    randBoolean,
    randSentence,
    randFirstName,
    randLastName,
    randUuid,
    randCurrencyCode,
} from '@ngneat/falso';

import { OrderStatesTypes } from '../../types/OrderStates';

// Helper function for price mock data
const mockOrderPrice = (overwrite?: Partial<OrderPrice>): OrderPrice => ({
    priceStyleNumber: randNumber({ min: 10000000, max: 99999999 }),
    purchasePrice: randNumber({ min: 10, max: 1000, fraction: 2 }),
    currencyCode: randCurrencyCode(),
    priceName: `Price ${randWord({ capitalize: true })}`,
    isDeleted: false,
    isActive: true,
    ...overwrite,
});

// Helper function for consistent company mock data
const mockOrderCompany = (overwrite?: Partial<OrderCompany>): OrderCompany => ({
    companyName: randCompanyName(),
    companyNumber: randNumber({ min: 400000, max: 500000 }).toString(),
    isDeleted: false,
    ...overwrite,
});

// Helper function for collection mock data
const mockOrderCollection = (
    overwrite?: Partial<OrderCollection>
): OrderCollection => ({
    collectionName: `${randNumber({ min: 2024, max: 2026 })} ${randWord().toUpperCase()} #${randNumber({ min: 1, max: 5 })}`,
    collectionNumber: randNumber({ min: 50000, max: 60000 }),
    collectionTerm: `${randNumber({ min: 20, max: 30 })} ${randWord().toUpperCase()} #${randNumber({ min: 1, max: 5 })}`,
    isDeleted: false,
    ...overwrite,
});

// Helper function for composition mock data
const mockOrderComposition = (
    overwrite?: Partial<OrderComposition>
): OrderComposition => ({
    composition: randWord(),
    hasBom: randBoolean(),
    isBomValid: randBoolean(),
    ...overwrite,
});

// Helper function for issuer mock data
const mockOrderIssuer = (overwrite?: Partial<OrderIssuer>): OrderIssuer => ({
    isDeleted: false,
    issuerName: randCompanyName(),
    issuerNumber: randNumber({ min: 100, max: 200 }),
    issuerShortName: randWord().toUpperCase(),
    ...overwrite,
});

// Helper function for order state mock data
const mockOrderState = (overwrite?: Partial<OrderState>): OrderState => {
    const orderStateTypeNameValues = Object.values(OrderStatesTypes);
    const randomOrderStateTypeName =
        orderStateTypeNameValues[
            Math.floor(Math.random() * orderStateTypeNameValues.length)
        ];

    return {
        isDeleted: false,
        orderStateName: randWord({ capitalize: true }),
        orderStateNumber: randNumber({ min: 1, max: 10 }),
        orderStateTypeName: randomOrderStateTypeName,
        ...overwrite,
    };
};

// Helper function for style mock data
const mockOrderStyle = (overwrite?: Partial<OrderStyle>): OrderStyle => ({
    styleName: randWord({ capitalize: true, length: 2 })
        .join(' ')
        .toUpperCase(),
    styleNumber: randNumber({ min: 10000000, max: 99999999 }),
    ...overwrite,
});

// Helper function for buyer mock data
const mockOrderUser = (overwrite?: Partial<OrderUser>): OrderUser => ({
    userName: `${randFirstName()} ${randLastName()}`,
    userNumber: randUuid(),
    ...overwrite,
});

// Helper function for label mock data
const mockOrderLabel = (overwrite?: Partial<OrderLabel>): OrderLabel => ({
    labelCode: randWord({ capitalize: true }),
    labelNumber: randNumber({ min: 100000, max: 999999 }),
    ...overwrite,
});

// Helper function for key order mock data
const mockOrderKeyOrder = (
    overwrite?: Partial<OrderKeyOrder>
): OrderKeyOrder => ({
    keyOrderName: randSentence({ length: 2 }).join(' '),
    keyOrderNumber: randNumber({ min: 1000, max: 9999 }),
    ...overwrite,
});

// Helper function for standard text mock data
const mockOrderStandardText = (
    overwrite?: Partial<OrderStandardText>
): OrderStandardText => ({
    isDeleted: randBoolean(),
    standardText: randSentence({ length: randNumber({ min: 3, max: 8 }) }).join(
        ' '
    ),
    standardTextNumber: randNumber({ min: 1, max: 9999 }),
    textName: randWord({ capitalize: true }),
    ...overwrite,
});

// Helper function for order header standard text mock data
const mockOrderHeaderStandardText = (
    overwrite?: Partial<OrderHeaderStandardText>
): OrderHeaderStandardText => ({
    standardText: randSentence({ length: randNumber({ min: 3, max: 8 }) }).join(
        ' '
    ),
    standardTextNumbers: Array.from(
        { length: randNumber({ min: 1, max: 5 }) },
        () => randNumber({ min: 1, max: 9999 })
    ),
    ...overwrite,
});

// Helper function for assortment mock data
const mockOrderAssortment = (
    overwrite?: Partial<OrderAssortment>
): OrderAssortment => ({
    assortmentNumber: randNumber({ min: 1000, max: 9999 }),
    assortmentName: `Assortment ${randNumber({ min: 1, max: 100 })}`,
    ...overwrite,
});

// Helper function for customer group mock data
const mockOrderCustomerGroup = (
    overwrite?: Partial<OrderCustomerGroup>
): OrderCustomerGroup => ({
    customerGroupNumber: randNumber({ min: 100, max: 999 }),
    customerGroupName: `Customer Group ${randNumber({ min: 1, max: 50 })}`,
    ...overwrite,
});

// Helper function for delivery period mock data
const mockOrderDeliveryPeriod = (
    overwrite?: Partial<OrderDeliveryPeriod>
): OrderDeliveryPeriod => ({
    deliveryPeriodNumber: randNumber({ min: 1, max: 12 }),
    deliveryPeriodName: `Period ${randNumber({ min: 1, max: 12 })}`,
    ...overwrite,
});

export {
    mockOrderPrice,
    mockOrderCompany,
    mockOrderCollection,
    mockOrderComposition,
    mockOrderIssuer,
    mockOrderState,
    mockOrderStyle,
    mockOrderUser,
    mockOrderLabel,
    mockOrderKeyOrder,
    mockOrderHeaderStandardText,
    mockOrderStandardText,
    mockOrderAssortment,
    mockOrderCustomerGroup,
    mockOrderDeliveryPeriod,
};
